import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getTankerUsers, createTankerUser, updateTankerUser, deleteTankerUser } from '../../../models/tankerModel';
import { getUsers } from '../../../models/userModel';

function TankerUsers() {
  const { user } = useAuth();
  const [tankerUsers, setTankerUsers] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    tanker_user_user: '',
    tanker_user_name: '',
    tanker_user_phone: '',
    tanker_user_status: 'active'
  });

  useEffect(() => {
    fetchTankerUsers();
    fetchSystemUsers();
  }, []);

  const fetchTankerUsers = async () => {
    try {
      setLoading(true);
      const data = await getTankerUsers();
      if (data.status === 1) {
        // API returns {status:1, data:{users:[]}}
        setTankerUsers(data.data?.users || []);
      } else {
        console.error('Failed to fetch tanker users:', data.message);
      }
    } catch (error) {
      console.error('Error fetching tanker users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemUsers = async () => {
    try {
      // Pass all=true to get all users without pagination for dropdown
      const params = new URLSearchParams({ all: 'true' });
      const data = await getUsers(params);
      if (data.status === 1) {
        // API returns all users when all=true parameter is set
        setSystemUsers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching system users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Transform the form data to match the API expectations
      const payload = editingUser 
        ? { 
            name: formData.tanker_user_name,
            phone: formData.tanker_user_phone,
            password: '', // Password update handled separately
            id: editingUser.tanker_user_id
          }
        : { 
            name: formData.tanker_user_name,
            phone: formData.tanker_user_phone,
            password: '', // Will be generated by system
            user_id: formData.tanker_user_user
          };

      const data = editingUser 
        ? await updateTankerUser(editingUser.tanker_user_id, payload)
        : await createTankerUser(payload);

      if (data.status === 1) {
        fetchTankerUsers();
        setShowAddModal(false);
        setEditingUser(null);
        setFormData({
          tanker_user_user: '',
          tanker_user_name: '',
          tanker_user_phone: '',
          tanker_user_status: 'active'
        });
      } else {
        alert(data.message || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error saving user');
    }
  };

  const handleEdit = (tankerUser) => {
    setEditingUser(tankerUser);
    setFormData({
      tanker_user_user: tankerUser.tanker_user_user || '',
      tanker_user_name: tankerUser.tanker_user_name || '',
      tanker_user_phone: tankerUser.tanker_user_phone || '',
      tanker_user_status: tankerUser.tanker_user_status || 'active'
    });
    setShowAddModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this tanker user?')) {
      return;
    }

    try {
      const data = await deleteTankerUser(userId);
      if (data.status === 1) {
        fetchTankerUsers();
      } else {
        alert(data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingUser(null);
    setFormData({
      tanker_user_user: '',
      tanker_user_name: '',
      tanker_user_phone: '',
      tanker_user_status: 'active'
    });
  };

  const getSystemUserName = (userId) => {
    const systemUser = systemUsers.find(u => u.user_id == userId);
    return systemUser ? systemUser.user_full_name : 'Unknown User';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Water Tanker App Users</h1>
                <p className="text-xs text-gray-500 mb-1">Manage users who have access to the water tanker mobile application</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {tankerUsers.length} user{tankerUsers.length !== 1 ? 's' : ''} total
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                  style={{ 
                    backgroundColor: '#0087D4', 
                    boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add New App User
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading tanker users...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      App Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Display Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      System User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tankerUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No tanker app users found. Add your first user to get started.
                      </td>
                    </tr>
                  ) : (
                    tankerUsers.map((tankerUser) => (
                      <tr key={tankerUser.tanker_user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {tankerUser.tanker_user_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {tankerUser.user_full_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getSystemUserName(tankerUser.tanker_user_user)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {tankerUser.tanker_user_phone || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            tankerUser.tanker_user_status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {tankerUser.tanker_user_status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(tankerUser)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                            {(user?.user_id === 1 || user?.is_it) && (
                              <button
                                onClick={() => handleDelete(tankerUser.tanker_user_id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit App User' : 'Add New App User'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    System User *
                  </label>
                  <select
                    required
                    value={formData.tanker_user_user}
                    onChange={(e) => setFormData({...formData, tanker_user_user: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a system user</option>
                    {systemUsers.map(systemUser => (
                      <option key={systemUser.user_id} value={systemUser.user_id}>
                        {systemUser.user_full_name} ({systemUser.user_email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    App Username *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.tanker_user_name}
                    onChange={(e) => setFormData({...formData, tanker_user_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., driver1"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.tanker_user_phone}
                    onChange={(e) => setFormData({...formData, tanker_user_phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., +27123456789"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.tanker_user_status}
                    onChange={(e) => setFormData({...formData, tanker_user_status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingUser ? 'Update' : 'Add'} User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TankerUsers; 